import { useRetryValidation } from '@/contexts/RetryValidationContext'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { executionService } from '@/lib/features/execution/ExecutionService'
import { extractExecutionIdFromNodeExecutionId } from '@/utils/workflow-utils'
import { getDisplayName, getToolColor, getToolIcon } from '@/utils/workflowToolMappings'
import { ChevronRight, Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Handle, Position } from 'reactflow'
import { NodeExecution } from 'sprites-types/api/v2'
import { RetryButton } from '../Trace/RetryButton'
import { RetryDropdownMenu } from '../Trace/RetryDropdownMenu'
import { FlowchartNode as FlowchartNodeType } from './layoutUtils'

interface FlowchartNodeProps {
  data: FlowchartNodeType
  selected: boolean
  onNodeClick?: (nodeId: string) => void
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
  isWorkflowExecuting?: boolean // Hide retry buttons if workflow is currently executing
  workflowConfig?: any
  workflowVersion?: number
  nodeExecutions?: Record<string, NodeExecution>
  workflowExecutionId?: string
}

function canViewNodeExecution(nodeExecution: NodeExecution): boolean {
  const validStatuses = ['completed', 'failed', 'errored']
  return validStatuses.includes(nodeExecution.status)
}

// Helper function to get file icon based on type
export function FlowchartNode({
  data,
  selected,
  onOpenPanel,
  isWorkflowExecuting = false,
  workflowConfig,
  workflowVersion,
  nodeExecutions,
  workflowExecutionId,
}: FlowchartNodeProps) {
  const toolId = data.toolId
  const { context, status, sendMessage } = useCopilotContext()
  const colorScheme = getToolColor(toolId)
  const IconComponent = getToolIcon(toolId)

  // Retry state
  const [isRetryDropdownOpen, setIsRetryDropdownOpen] = useState(false)
  const retryButtonRef = useRef<HTMLButtonElement | null>(null)
  const nodeRef = useRef<HTMLDivElement | null>(null)

  // Get retry validation context (optional)
  const retryValidation = useRetryValidation()

  // Check if this is a visual start or end node
  const isVisualNode = data.visualNode === true

  // Check if this is the start node
  const isStartNode = data.id === '__start__'

  // Check if this node has emitMode: "multi" to show stacked card effect
  const hasMultiEmitMode = data.inputs?.emitMode === 'multi'

  // Get node execution results - filter by current workflow execution ID
  const nodeExecution = nodeExecutions
    ? Object.entries(nodeExecutions).find(([executionId, exec]: [string, NodeExecution]) => {
        // Match by node ID
        if (exec.nodeId !== data.id) return false

        // If we have a current workflow execution ID, filter by it
        if (workflowExecutionId) {
          try {
            const execWorkflowId = extractExecutionIdFromNodeExecutionId(executionId)
            return execWorkflowId === workflowExecutionId
          } catch (error) {
            // If we can't parse the execution ID, exclude it
            console.warn('Failed to parse node execution ID:', executionId, error)
            return false
          }
        }

        // If no workflow execution ID, accept any execution for this node
        return true
      })?.[1]
    : null

  // Process the node execution outputs
  let outputsCount: number = 0
  let executionStatus = null

  if (nodeExecution?.outputs) {
    executionStatus = nodeExecution.status
    const nodeResults = nodeExecution.outputs

    outputsCount = Array.isArray(nodeResults)
      ? nodeResults.length
      : !!nodeResults
        ? Object.entries(nodeResults).length
        : 0
  }

  // Check if node is currently executing
  const isExecuting = nodeExecution?.status === 'running' || nodeExecution?.status === 'pending'

  // Retry handler - sends message to chat instead of calling API
  const handleRetry = (
    nodeExecutionId: string,
    resumeStage: 'tool_execution' | 'edge_selection'
  ) => {
    // Prevent retry if connection is active
    if (status === 'streaming') {
      toast.warn('Cannot retry while Copilot is actively responding')
      return
    }

    const message = `Rerun this execution: ${nodeExecutionId}, at ${resumeStage}`

    sendMessage(message)
    setIsRetryDropdownOpen(false)
  }

  const validationResult = nodeExecution
    ? retryValidation?.validationResults?.[nodeExecution.executionId]
    : undefined
  const availableStages = validationResult?.availableStages || []

  // Show button if we have at least one available stage and no active connection
  const shouldShowRetryButton = !isWorkflowExecuting && availableStages.length > 0

  // Handler for viewing workflow inputs
  const handleViewWorkflowInputs = async () => {
    if (!workflowExecutionId || !onOpenPanel) return

    try {
      // First check if inputs are available in context
      const contextInputs = context.workflowExecutionInputs

      if (contextInputs) {
        onOpenPanel(
          {
            type: 'workflow-inputs',
            inputs: contextInputs,
            executionId: workflowExecutionId,
          },
          [{ name: 'Workflow Inputs', index: 0 }]
        )
      } else {
        // Fallback to fetching from API if not in context
        const executionData = await executionService.getExecutionData(workflowExecutionId)
        onOpenPanel(
          {
            type: 'workflow-inputs',
            inputs: executionData.executionData.inputs,
            executionId: workflowExecutionId,
          },
          [{ name: 'Workflow Inputs', index: 0 }]
        )
      }
    } catch (error) {
      console.error('Failed to fetch workflow inputs:', error)
      toast.error('Failed to load workflow inputs')
    }
  }

  // Close dropdown when clicking outside the node
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isRetryDropdownOpen) return

      const target = event.target as Element
      const clickedInsideNode = nodeRef.current?.contains(target)

      // Check if clicked inside dropdown by checking for portal dropdown elements
      // The dropdown is portaled, so we check if any parent has the fixed positioning and high z-index
      let clickedInsideDropdown = false
      let element = target
      while (element && element !== document.body) {
        const style = window.getComputedStyle(element)
        if (style.position === 'fixed' && style.zIndex === '9999') {
          clickedInsideDropdown = true
          break
        }
        element = element.parentElement as Element
      }

      // Close if clicked outside both node and dropdown
      if (nodeRef.current && !clickedInsideNode && !clickedInsideDropdown) {
        setIsRetryDropdownOpen(false)
      }
    }

    if (isRetryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside, true)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isRetryDropdownOpen])

  return (
    <div className="relative" ref={nodeRef}>
      <div>
        {/* Stacked card effect for multi emit mode */}
        {hasMultiEmitMode && (
          <>
            {/* Back card layer */}
            <div className="absolute left-1 top-1 h-full w-[320px] rounded-2xl bg-gray-100/80 shadow-lg" />
            {/* Middle card layer */}
            <div
              className="absolute left-0.5 top-0.5 h-full w-[320px] rounded-2xl shadow-xl"
              style={{ backgroundColor: colorScheme.backgroundColor }}
            />
          </>
        )}

        {/* Animated border/glow for executing nodes */}
        {isExecuting && (
          <div
            className="absolute -inset-1 rounded-2xl"
            style={{
              background:
                'linear-gradient(135deg, rgb(59 130 246 / 0.6) 0%, rgb(59 130 246 / 0.2) 100%)',
              filter: 'blur(6px)',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }}
          />
        )}

        {/* Main card */}
        <div
          className={`relative flex w-[320px] flex-col rounded-2xl border bg-white transition-all duration-300 ease-out ${
            selected
              ? 'scale-[1.02] border-blue-200 shadow-2xl ring-2 ring-blue-500/10'
              : isVisualNode
                ? 'cursor-default border-gray-200 shadow-md'
                : 'border-gray-200 shadow-md hover:scale-[1.01] hover:border-gray-300 hover:shadow-xl'
          } ${isExecuting ? 'border-blue-400/50' : ''}`}
          style={{
            boxShadow: selected
              ? '0 25px 50px -12px rgb(0 0 0 / 0.25), 0 0 0 1px rgb(59 130 246 / 0.05)'
              : '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
          }}
        >
          <Handle type="target" id="top" position={Position.Top} style={{ opacity: 0 }} />
          <Handle type="target" id="left" position={Position.Left} style={{ opacity: 0 }} />
          <Handle type="target" id="right" position={Position.Right} style={{ opacity: 0 }} />
          <Handle type="target" id="bottom" position={Position.Bottom} style={{ opacity: 0 }} />

          {/* Header section */}
          <div className="flex items-center justify-between p-5 pb-4">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <div
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 text-white shadow-lg backdrop-blur-sm"
                style={{
                  background: `linear-gradient(135deg, ${colorScheme.color}dd 0%, ${colorScheme.color} 100%)`,
                  boxShadow: `0 4px 12px ${colorScheme.color}30, 0 0 0 1px ${colorScheme.color}20`,
                }}
              >
                <IconComponent size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold leading-tight text-gray-900">
                  {data.name}
                </h3>
              </div>
            </div>
          </div>

          {/* Tool section */}
          {data.toolId && !isVisualNode && (
            <div className="px-5 pb-3">
              <div
                className="flex items-center justify-center rounded-xl border px-4 py-2.5 backdrop-blur-sm"
                style={{
                  background: `linear-gradient(135deg, ${colorScheme.backgroundColor} 0%, ${colorScheme.backgroundColor}cc 100%)`,
                  borderColor: `${colorScheme.color}30`,
                  boxShadow: `0 2px 8px ${colorScheme.color}15, inset 0 1px 0 ${colorScheme.color}10`,
                }}
              >
                <span
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-bold uppercase tracking-wider"
                  style={{ color: colorScheme.color }}
                >
                  {getDisplayName(data.toolId)}
                </span>
              </div>
            </div>
          )}

          {/* Description section */}
          {data.description && (
            <div className="px-5 pb-4">
              <p className="line-clamp-3 text-sm font-medium leading-relaxed text-gray-600">
                {data.description}
              </p>
            </div>
          )}

          {/* Buttons for start node */}
          {isStartNode && (
            <div className="space-y-2 px-5 pb-4">
              {/* View Workflow Details button - shows workflow overview */}
              {workflowConfig && onOpenPanel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenPanel(
                      {
                        type: 'workflow-details',
                        workflowConfig,
                        workflowVersion,
                      },
                      [{ name: workflowConfig.name || 'Workflow Details', index: 0 }]
                    )
                  }}
                  className="group flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2 transition-all duration-200 hover:border-gray-300 hover:bg-gray-100 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                >
                  <Info size={14} className="flex-shrink-0 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-700">View Workflow Details</span>
                </button>
              )}

              {/* View Workflow Inputs button - only show if workflow is executing */}
              {workflowExecutionId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleViewWorkflowInputs()
                  }}
                  className="group flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2 transition-all duration-200 hover:border-blue-300 hover:bg-blue-100 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <span className="text-xs font-semibold text-blue-700">View Workflow Inputs</span>
                  <ChevronRight
                    size={14}
                    className="flex-shrink-0 text-blue-500 transition-all duration-200 group-hover:translate-x-0.5"
                  />
                </button>
              )}
            </div>
          )}

          {/* Execution Details section - only show if node has execution data */}
          {nodeExecution && outputsCount > 0 && (
            <div className="border-t border-gray-100 px-5 pb-4 pt-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">Execution Details</span>
                {executionStatus && (
                  <span
                    className={`rounded-full px-3 py-1.5 text-xs font-bold ${
                      executionStatus === 'completed'
                        ? 'border border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 shadow-sm'
                        : executionStatus === 'failed'
                          ? 'border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 shadow-sm'
                          : 'border border-red-200 bg-gradient-to-r from-red-50 to-red-100 text-red-700 shadow-sm'
                    }`}
                  >
                    {executionStatus}
                  </span>
                )}
              </div>

              <div className="mb-3 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Execution Time */}
                  {nodeExecution.startedAt && nodeExecution.completedAt && (
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                      {(() => {
                        const startTime = new Date(nodeExecution.startedAt).getTime()
                        const endTime = new Date(nodeExecution.completedAt).getTime()
                        const durationMs = endTime - startTime

                        if (durationMs < 1000) {
                          return `${durationMs}ms`
                        } else if (durationMs < 60000) {
                          return `${Math.round(durationMs / 1000)}s`
                        } else if (durationMs < 3600000) {
                          const minutes = Math.floor(durationMs / 60000)
                          const seconds = Math.round((durationMs % 60000) / 1000)
                          return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
                        } else {
                          const hours = Math.floor(durationMs / 3600000)
                          const minutes = Math.round((durationMs % 3600000) / 60000)
                          return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
                        }
                      })()}
                    </span>
                  )}

                  {/* Credits */}
                  {!!nodeExecution.credits && nodeExecution.credits > 0 && (
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                      {nodeExecution.credits} credits
                    </span>
                  )}

                  {/* Emit Mode */}
                  {nodeExecution.inputs?.functionArgs?.emitMode && (
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                      {nodeExecution.inputs.functionArgs.emitMode}
                    </span>
                  )}
                </div>

                {/* Retry Button with Dropdown */}
                {shouldShowRetryButton && (
                  <div className="relative">
                    <RetryButton
                      ref={retryButtonRef}
                      onClick={() => setIsRetryDropdownOpen(!isRetryDropdownOpen)}
                      isRetrying={false}
                    />
                    <RetryDropdownMenu
                      nodeExecution={nodeExecution}
                      availableStages={availableStages}
                      onRetry={handleRetry}
                      onClose={() => setIsRetryDropdownOpen(false)}
                      isOpen={isRetryDropdownOpen}
                      buttonRef={retryButtonRef}
                    />
                  </div>
                )}
              </div>

              {/* Output summary */}
              <div className="text-sm font-medium text-gray-600">
                {outputsCount === 1 ? '1 result generated' : `${outputsCount} results generated`}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!isVisualNode && (
            <div className="border-t border-gray-100 px-5 pb-5 pt-4">
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (onOpenPanel) {
                      onOpenPanel(
                        {
                          type: 'node-execution-details',
                          nodeId: data.id,
                          executionId: nodeExecution?.executionId,
                          nodeExecution: nodeExecution,
                          nodeData: data,
                          workflowConfig,
                        },
                        [{ name: data.name || 'Node Details', index: 0 }]
                      )
                    }
                  }}
                  className="group relative flex flex-1 items-center justify-center gap-2 overflow-hidden rounded-xl border border-gray-200/80 bg-gradient-to-br from-white to-gray-50/50 px-4 py-3 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400/20"
                >
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-700">
                    Details
                  </span>
                  <ChevronRight
                    size={14}
                    className="flex-shrink-0 text-gray-400 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-600"
                  />
                </button>

                {workflowConfig &&
                  data.toolId &&
                  nodeExecution &&
                  canViewNodeExecution(nodeExecution) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (onOpenPanel) {
                          onOpenPanel(
                            {
                              type: 'workflow-tool-result',
                              nodeExecution,
                              workflowConfig,
                            },
                            [{ name: data.name || 'Tool Result', index: 0 }]
                          )
                        }
                      }}
                      className="group relative flex flex-1 items-center justify-center gap-2 overflow-hidden rounded-xl border border-blue-200/80 bg-gradient-to-br from-blue-50 to-blue-100/50 px-4 py-3 shadow-sm transition-all duration-200 hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                    >
                      <span className="text-xs font-bold uppercase tracking-wide text-blue-700">
                        Results
                      </span>
                      <ChevronRight
                        size={14}
                        className="flex-shrink-0 text-blue-400 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-blue-600"
                      />
                    </button>
                  )}
              </div>
            </div>
          )}
          <Handle type="source" id="top" position={Position.Top} style={{ opacity: 0 }} />
          <Handle type="source" id="bottom" position={Position.Bottom} style={{ opacity: 0 }} />
          <Handle type="source" id="left" position={Position.Left} style={{ opacity: 0 }} />
          <Handle type="source" id="right" position={Position.Right} style={{ opacity: 0 }} />
        </div>
      </div>
    </div>
  )
}
